/**
 * Decides which admin app serves a given sidebar entry.
 *
 * The sidebar is built at runtime from the backend `navigation` response, and
 * its `url_path` values are shared by both admin apps — the backend has no way
 * to express "this screen now lives in v2". So the frontend owns that mapping:
 * anything not listed here is prefixed with the legacy admin host, which is why
 * a screen can be fully ported, merged and deployed and still open the old app.
 *
 * Adding a path below is the deliberate act of cutting that screen over.
 */

/**
 * Paths this app serves itself. Everything else goes to the legacy admin.
 *
 * This is an explicit list rather than a lookup against the v2 router, for two
 * reasons that both matter:
 *
 * 1. The router registers a catch-all (`/:pathMatch(.*)*`), so resolving a path
 *    against it matches *everything* — the check would send the whole menu to
 *    v2.
 * 2. Several routes are registered but still dark-launched (`/tracking`,
 *    `/delivery-box`, `/order-barcode`, `/generate-drawing-sheet`,
 *    `/buying-groups-sales-reps`). A router-aware check would cut all of them
 *    over at once, without anyone reviewing them.
 */
export const V2_OWNED_PATHS: readonly string[] = [
  '/default-categories',
  '/default-products', // Default Products board: categories, products and their order in one view.
  '/resellers-management',
  // Served by this app on preprod before the Settings pilot arrived: the sidebar
  // used to hard-code it alongside the two paths above. Kept through the master
  // promotion so this merge does not push it back to the legacy app.
  '/ai-analytics',
  // Settings pilot (AB#20755) — screens ported in PR 15329.
  '/general-settings',
  '/email-configuration',
  '/ui-branding',
  '/desk-messages', // AB#21836
  // Price List, SKU & Factories (AB#20756) — screens ported in PR 15682.
  '/price-list',
  '/factories',
  '/sku',
  // Categories, Design Categories & Brand (AB#20757) — screens ported in PR 15822.
  '/brands',
  '/design-categories',
  '/products/categories',
  // Translations, Email & Video Templates (AB#20758) — screens ported in PR 15849.
  '/translations',
  '/email-templates',
  '/video-templates',
  '/default-video-templates',
  '/video-usage',
  // Companies, Users, Roles & Permissions (AB#20754) — screens ported in PRs 15878, 15880, 15881.
  '/companies',
  '/users',
  '/roles',
  '/permissions',
  '/audit-log', // AB#22077
  '/product-development', // AB#22092
  '/product-development/queue', // AB#22760
  '/product-development/board', // AB#22638
  '/product-development/requests', // AB#22169
  '/product-development/style-codes', // AB#22170
  '/product-development/live-patterns', // AB#22171
  '/product-development/approvals', // AB#22172
  '/product-development/notices', // AB#22173
  '/product-requests',
  '/product-development/insights',
  '/product-development/setup',
  // Long-tail screens (AB#20759) and Customers (AB#21152) — ported in PRs 15931, 15933,
  // 15938, dark-launched on 12 Sep and cut over once each was exercised on the portal.
  '/general-action',
  '/products/data-source',
  '/customers',
  // Logo library (AB#22214) — new v2 screen, menu entry injected in the store.
  '/logos',
];

/**
 * The merchant roles: the backend's company admin (`custimoo.default_roles.company-admin`,
 * "admin") and `MerchantGroupAdmin` (`custimoo.custom_roles`). Matched like the backend's
 * `MerchantGroupScope::normalize`: case-insensitive, spaces ignored.
 */
export const MERCHANT_ROLES: readonly string[] = ['admin', 'MerchantGroupAdmin'];

const normalizeRole = (name: string): string => name.replace(/ /g, '').toLowerCase();

export const isMerchantRole = (roleName: string | null | undefined): boolean =>
  !!roleName && MERCHANT_ROLES.some((role) => normalizeRole(role) === normalizeRole(roleName));

/**
 * Owned paths that this app serves to staff only. The sidebar row is shared — every role's
 * "All Products" points at `/products` — but merchants see a different screen there (their own
 * products, custom titles, Active switch, sort, e-commerce sync, `/products/update-company-fields`).
 * Until that merchant screen is ported (product-setup PLAN P2.25-P2.27), merchants keep the
 * legacy one even after `/products` is cut over. Both merchant roles hold
 * `access-new-dashboard` and the products row.
 */
export const STAFF_ONLY_PATHS: readonly string[] = ['/products'];

/**
 * Whether this app serves the path itself — to this role. `roleName` is the signed-in user's
 * role (`store.role?.name`); without it the answer is the plain ownership list.
 *
 * Every place that can send the user to a screen — the sidebar, the Quick Access
 * menu, any future shortcut — has to ask this same question. A link that decides on
 * its own is how a fully ported screen keeps opening the legacy app: the sidebar
 * lands you on the v2 page while the shortcut next to it bounces you back to v1.
 */
export const isV2OwnedPath = (
  path: string | null,
  roleName?: string | null,
  owned: readonly string[] = V2_OWNED_PATHS
): path is string =>
  path !== null &&
  owned.includes(path) &&
  !(STAFF_ONLY_PATHS.includes(path) && isMerchantRole(roleName));

/**
 * Product setup revamp, pre-prod clickable build (AB#21734): builds with
 * `VITE_PRODUCT_SETUP_V2=true` register the new `/products` screens (router), and staff who
 * hold `product-setup-v2` (the routes' `meta.permission`) open them from the menu. Everyone
 * else, and every other build, keeps the legacy `/products`. Merchants keep it through
 * `STAFF_ONLY_PATHS`.
 */
export const PRODUCT_SETUP_PATHS: readonly string[] = ['/products'];

export const ownedPathsFor = (productSetup: boolean): readonly string[] =>
  productSetup ? [...V2_OWNED_PATHS, ...PRODUCT_SETUP_PATHS] : V2_OWNED_PATHS;

/** Whether this build serves the new product setup to a user who holds `product-setup-v2`. */
export const productSetupMenuEnabled = (
  holdsPermission: boolean,
  flag: string | undefined = import.meta.env.VITE_PRODUCT_SETUP_V2 as string | undefined
): boolean => flag === 'true' && holdsPermission;

/** The paths this app serves to a user with these permissions (null: the plain list). */
export const ownedPathsForUser = (
  permissions?: readonly string[] | null,
  flag?: string
): readonly string[] =>
  ownedPathsFor(productSetupMenuEnabled(!!permissions?.includes('product-setup-v2'), flag));

/**
 * Resolve a backend-supplied menu path to a href.
 *
 * Owned paths return the bare path, so vue-router handles them in this app.
 * Everything else is prefixed with the legacy host, preserving the previous
 * behaviour exactly — including for a null `url_path`, which the backend model
 * allows and which previously produced `${legacyBaseUrl}null`.
 */
export const resolveMenuHref = (
  path: string | null,
  legacyBaseUrl: string,
  roleName?: string | null,
  permissions?: readonly string[] | null,
  flag?: string
): string =>
  isV2OwnedPath(path, roleName, ownedPathsForUser(permissions, flag)) ? path : legacyBaseUrl + path;
